
// TODO: must stop video on slide change
/**
 * `VideoPlayer` - This video component will be placed in all components that require a video media block.
 * The component can play HTML5 and Youtube videos, it uses Plyr library to manage the video settings and controls.
 * It contains a thumbnail and the player, it can be played inside a modal or after the play button is clicked behind
 * the thumbnail element.
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { VideoCustomEvent, VideoPlayerComponent, VideoPlayerMixinsDataJSON } from './VideoPlayer';
import ThumbnailComponent from './Thumbnail.vue';
import PlayerComponent from './Player.vue';
import Plyr, { PlyrEvent, StandardEvent } from 'plyr';
import { CUSTOM_EVENTS, PLYR_EVENTS } from './VideoPlayer.config';
import OwcLightbox from '@owc-packages/Lightbox/src/Lightbox.vue';
import { getComponentStateConnection } from '@owc-utils/Store/owcComponent';

import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';

export default (ComponentInitializeDispatcher as VideoPlayerComponent).extend({
  name: 'owc-video-player',
  components: {
    ThumbnailComponent,
    PlayerComponent,
    OwcLightbox,
  },
  mixins: [OwcDataComponent],
  data() {
    return {
      modalIsOpen: false,
      player: null,
      isVideoPlaying: false,
      isHeightSet: false,
      hasUserInteracted: false,
      playerReady: false,
    };
  },
  mounted() {
    if (this.componentData) {
      this.setThumbnailData(this.componentData);
    }
  },
  computed: {
    thumbnailId: function() {
      return this.componentId ? `${this.componentId}-thumbnail` : null;
    },
    videoDetails: function() {
      return this.componentData?.videoDetails;
    },
    thumbnail: function() {
      return this.componentData?.thumbnail;
    },
    playerOverlayText: function() {
      return this.componentData?.playerOverlayText;
    },
    openInModal: function() {
      return Boolean(this.videoDetails?.openInModal);
    },
    isDarkMode: function() {
      return Boolean(this.componentData?.darkMode);
    },
  },
  methods: {
    buildEventPayload() {
      return {
        detail: {
          playerId: this.componentId,
        },
        composed: true,
        bubbles: true,
      };
    },
    async setThumbnailData(data: VideoPlayerMixinsDataJSON) {
      if (this.thumbnailId && this.thumbnail) {
        const payload = {
          id: this.thumbnailId,
          name: 'owc-video-player-thumbnail',
          json: {
            height: data?.height,
            genericImage: {
              ...this.thumbnail,
            },
          },
        };
        const connection = await getComponentStateConnection();
        connection.updateComponent(payload);
      }
    },
    handlePlayerReady(player: Plyr) {
      this.playerReady = true;
      this.player = player;

      this.player.on(PLYR_EVENTS.ended as StandardEvent, (): void => {
        this.isVideoPlaying = false;
        if (this.openInModal) {
          this.modalIsOpen = false;
        }
      });
      const eventPayload = this.buildEventPayload();
      this.player.on(PLYR_EVENTS.play as StandardEvent, () => {
        this.$el.dispatchEvent(new CustomEvent(CUSTOM_EVENTS.start, eventPayload));
      });
      this.player.on(PLYR_EVENTS.pause as StandardEvent, () => {
        this.$el.dispatchEvent(new CustomEvent(CUSTOM_EVENTS.stop, eventPayload));
      });
      this.$el.dispatchEvent(new CustomEvent<VideoCustomEvent>(CUSTOM_EVENTS.ready, eventPayload));

      this.handleVideoTracking();

      //This is for the first play when the user accepts the consent
      this.playVideo();
    },
    handlePlayVideo(): void {
      this.hasUserInteracted = true;
      this.isVideoPlaying = true;

      // Else, player is initialized and play starts from there
      if (this.playerReady) {
        this.playVideo();
      }
    },
    handleCloseLightbox(): void {
      this.modalIsOpen = false;
      if (this.player) {
        this.isVideoPlaying = false;
        this.player.pause();
      }
      this.$el.dispatchEvent(new CustomEvent(CUSTOM_EVENTS.stop, this.buildEventPayload()));
    },
    playVideo(): void {
      if (this.openInModal && !this.modalIsOpen) {
        this.modalIsOpen = true;
      }
      if (this.player) {
        this.player?.playing ? this.player.pause() : this.player?.play();
      }
    },
    handleVideoTracking(): void {
      this.handleCustomTrackingDetailed(
        this.$el,
        {
          label: this.player?.source.title || this.componentData?.title,
          category: 'video',
          action: `video_${this.player?.provider}.video_start`,
        },
        'click',
      );

      const shouldTrackAndDetach = (plyr: Plyr, percentageInterval: number[]): boolean => {
        const currentTime = plyr.currentTime;
        const currentPercent = (100 * currentTime) / plyr.duration;
        if (currentPercent < percentageInterval[0] || currentPercent > percentageInterval[1]) {
          return false;
        }

        this.handleCustomTrackingDetailed(
          this.$el,
          {
            label: plyr.source.title || this.componentData?.title,
            category: 'video',
            action: `video_${plyr.provider}.video_${percentageInterval[0]}`,
          },
          'click',
        );
        return true;
      };
      const currentTimeCallback25 = (ev: PlyrEvent) => {
        if (shouldTrackAndDetach(ev.detail.plyr, [25, 49])) {
          ev.detail.plyr.off('timeupdate', currentTimeCallback25);
        }
      };
      const currentTimeCallback50 = (ev: PlyrEvent) => {
        if (shouldTrackAndDetach(ev.detail.plyr, [50, 89])) {
          ev.detail.plyr.off('timeupdate', currentTimeCallback25);
          ev.detail.plyr.off('timeupdate', currentTimeCallback50);
        }
      };
      const currentTimeCallback90 = (ev: PlyrEvent) => {
        if (shouldTrackAndDetach(ev.detail.plyr, [90, 100])) {
          ev.detail.plyr.off('timeupdate', currentTimeCallback25);
          ev.detail.plyr.off('timeupdate', currentTimeCallback50);
          ev.detail.plyr.off('timeupdate', currentTimeCallback90);
        }
      };
      this.player?.on('timeupdate', currentTimeCallback25);
      this.player?.on('timeupdate', currentTimeCallback50);
      this.player?.on('timeupdate', currentTimeCallback90);
    },
  },
  watch: {
    componentData(data) {
      //FIXME: backwards compatibility - remove on refactor to OWC_COMPONENT_REGISTRY
      if (this.componentId && data && data.height && !this.isHeightSet) {
        this.isHeightSet = true;
        this.setThumbnailData(data);
      }
    },
  },
});
